import type { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { ROUTES } from '@metal/shared'
import { Skeleton } from '@metal/ui'
import { useSession } from '@metal/ui/session'

export type GuardKind = 'public' | 'authenticated' | 'member' | 'seller' | 'buyer'

export function RouteGuard({
  kind,
  permission,
  children,
}: {
  kind: GuardKind
  permission?: string
  children: ReactNode
}) {
  const { user, loading } = useSession()
  const location = useLocation()

  if (kind === 'public') return <>{children}</>

  if (loading) {
    return (
      <div className="page">
        <Skeleton h={28} w="220px" />
        <Skeleton h={240} />
      </div>
    )
  }

  if (!user) {
    return <Navigate to={ROUTES.login(location.pathname + location.search)} replace />
  }

  // A page that asks only for a signed in account stays open to a half // finished file.
  if (kind === 'authenticated') return <>{children}</>

  // Anything past that needs a completed profile.
  if (user.membership !== 'active') {
    return <Navigate to={ROUTES.register()} replace />
  }

  if (kind === 'seller' && !user.sides.includes('sell')) {
    return <Navigate to={ROUTES.forbidden()} replace />
  }
  if (kind === 'buyer' && !user.sides.includes('buy')) {
    return <Navigate to={ROUTES.forbidden()} replace />
  }
  if (permission && !user.permissions.includes(permission)) {
    return <Navigate to={ROUTES.forbidden()} replace />
  }

  return <>{children}</>
}

/** A member landing on the public root goes to their dashboard. */
export function RootRedirect({ children }: { children: ReactNode }) {
  const { user, loading } = useSession()
  if (loading) return <>{children}</>
  if (user && user.membership === 'active') return <Navigate to={ROUTES.dashboard()} replace />
  return <>{children}</>
}
